import { useEffect, useRef } from "react";

/**
 * PerspectiveGrid — an endless grid scrolling toward the horizon (canvas, light-mode friendly).
 *
 * Mirrors the prop names of React Bits Pro's <PerspectiveGrid> so the official component can
 * replace this file later without touching the call site.
 *
 *  horizon : where the grid starts to appear, as a fraction of the height (top part stays empty)
 *  vanish  : where the lines actually converge (above `horizon`, so the lines fan out wide)
 *  The grid fades in just under `horizon` and dissolves again toward the bottom edge.
 */
export default function PerspectiveGrid({
  width = "100%",
  height = 400,
  speed = 0.5,
  color = "#3AA0FF",
  gridScale = 1,
  lineThickness = 2.5,
  autoPlay = true,
  opacity = 1,
  horizon = 0.44,
  vanish = 0.28,
  bottomFade = true, // set false / "" to keep the grid solid to the bottom edge
  className = "",
  children,
}) {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) return;
    const ctx = canvas.getContext("2d");

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let w = 0, h = 0, dpr = 1;
    let raf = 0, last = 0, frac = 0.8;
    let visible = true;

    function draw() {
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);
      if (!w || !h) return;

      const hy = h * horizon;        // grid becomes visible here
      const yv = h * vanish;         // lines converge here
      const D = h - yv;              // vanishing point → bottom edge
      const cx = w / 2;
      const sH = (w * 0.058) / gridScale;       // vertical-line spacing at the horizon row
      const sB = sH * D / (hy - yv);            // …and at the bottom edge

      ctx.lineWidth = lineThickness;
      ctx.strokeStyle = color;
      ctx.globalAlpha = opacity;
      ctx.lineCap = "butt";

      // vertical lines (fan out from the vanishing point)
      const n = Math.ceil(w / 2 / sH) + 1;
      ctx.beginPath();
      for (let i = -n; i <= n; i++) {
        ctx.moveTo(cx + i * sH, hy);
        ctx.lineTo(cx + i * sB, h);
      }
      ctx.stroke();

      // horizontal lines: constant spacing in depth, y = vanish + D / z
      const zMax = D / (hy - yv) + 1;
      ctx.beginPath();
      for (let j = 0; ; j++) {
        const z = 1 + (j - frac) / gridScale;
        if (z > zMax) break;
        if (z < 1) continue;                    // already past the bottom edge
        const y = yv + D / z;
        ctx.moveTo(0, y);
        ctx.lineTo(w, y);
      }
      ctx.stroke();
      ctx.globalAlpha = 1;
    }

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = wrap.clientWidth;
      h = wrap.clientHeight;
      canvas.width = Math.max(1, Math.round(w * dpr));
      canvas.height = Math.max(1, Math.round(h * dpr));
      canvas.style.width = w + "px";
      canvas.style.height = h + "px";
      draw();
    };

    const tick = (now) => {
      raf = requestAnimationFrame(tick);
      if (!visible || !autoPlay || reduce) { last = now; return; }
      const dt = Math.min(0.05, (now - last) / 1000 || 0);
      last = now;
      frac = (frac + dt * speed * 0.6 * gridScale) % 1;
      draw();
    };

    const ro = new ResizeObserver(resize);
    ro.observe(wrap);
    const io = new IntersectionObserver(([e]) => { visible = e.isIntersecting; });
    io.observe(wrap);

    resize();
    raf = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); io.disconnect(); };
  }, [speed, color, gridScale, lineThickness, autoPlay, opacity, horizon, vanish]);

  // Fade in just under the horizon, hold, then dissolve toward the bottom edge.
  const p = (v) => (v * 100).toFixed(1) + "%";
  const a = horizon;
  const b = horizon + 0.17 * (1 - horizon);
  const c = horizon + 0.3 * (1 - horizon);
  const mask = bottomFade
    ? `linear-gradient(to bottom, transparent ${p(a)}, #000 ${p(b)}, #000 ${p(c)}, transparent 100%)`
    : `linear-gradient(to bottom, transparent ${p(a)}, #000 ${p(b)})`;

  return (
    <div ref={wrapRef} className={`relative overflow-hidden ${className}`} style={{ width, height }}>
      <canvas
        ref={canvasRef}
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{ WebkitMaskImage: mask, maskImage: mask }}
      />
      {children}
    </div>
  );
}
